import React, { useState, useEffect } from 'react';
import { withNamespaces } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { withRouter } from 'react-router-dom';
import * as GlobalModalActions from '../../../redux/actions/GlobalModalActions';
import { showError, showSuccess } from '../../../redux/actions/GlobalToastActions';
import { Loading, Stepper } from '../../../components/common';
import { stepsName, companyName } from '../../../utils/Constants';
import AppUrls from '../../../config/AppUrls';
import { getCompany, getLegalRepresentative, getTermsAndConditions } from '../../../redux/reducers/ReferralReducer';
import { isLoading } from '../../../redux/reducers/GlobalReducer';
import { Routes as configRouter } from '../../../config/Routes';
import ModalTypes from '../../../components/Modal/ModalTypes';
import MailboxIcon from '../../../assets/img/rich_icons/mailbox.svg';
import NotFound from '../../Generic/NotFound';

const SignUpReferrals = (props) => {
  const { t, legalRepresentative, company, termsAndConditions, loading, onSubmit, children, location, match } = props;
  const { params } = match;
  const [activeStep, setActiveStep] = useState(stepsName.LEGAL_REPRESENTATIVE);
  const lastStep = stepsName.TERMS_CONDITIONS;
  const [loadingC, setLoadingC] = useState(false);
  const [referralIdParam, setReferralIdParam] = useState();
  const [externalSubMerchantId, setExternalSubMerchantId] = useState();

  const childrenArray = React.Children.toArray(children);

  const isOlaClick = location.pathname.includes('olaclick');

  const olaClickModalLabels = {
    title: 'labelOlaClickSuccessfulRegistration',
    labelConfirm: 'labelSignUpOlaClickSuccess',
    secondLabelConfirm: 'labelSignUpOlaClickSuccessSubtitle',
    textBtnConfirm: 'buttonOlaClickContinue',
  };

  const defaultModalLabels = {
    title: 'labelSuccessfulRegistration',
    labelConfirm: 'labelSignUpSuccess',
    secondLabelConfirm: 'labelSignUpSuccessSubtitle',
    textBtnConfirm: 'buttonContinue',
  };

  const customModalLabels = isOlaClick ? olaClickModalLabels : defaultModalLabels;

  useEffect(() => {
    setUrlParams();
  }, []);

  const setUrlParams = () => {
    const {
      route: {
        location: { search },
      },
    } = props;
    setReferralIdParam(new URLSearchParams(search).get('referralId') || new URLSearchParams(search).get('referralID'));
    setExternalSubMerchantId(new URLSearchParams(search).get('externalSubMerchantId') || new URLSearchParams(search).get('externalSubMerchantID'));
  };

  const handleCreateReferral = async () => {
    const request = getRequest();
    const {
      showModal,
      hideModal,
      route: { history },
      showError,
    } = props;

    try {
      setLoadingC(true);
      await onSubmit(request);
      setLoadingC(false);
    } catch (error) {
      setLoadingC(false);
      const { response = {} } = error;
      const msgError = (response.data && response.data.description) || t('labelNetworkError');
      showError(msgError);
      if (window.parent) {
        window.parent.postMessage('error', '*');
      }
      return;
    }
    if (window.parent) {
      window.parent.postMessage('success', '*');
    }
    showModal(
      {
        ...customModalLabels,
        mainIcon: MailboxIcon,
        headerIcon: 'check-circle',
        type: 'info',
        emailConfirmation: true,
        reverseButtons: false,
        disableCancel: true,
        loadingOnConfirm: true,
        onConfirm: () => {
          hideModal();
          history.push(configRouter.LOGIN);
        },
      },
      ModalTypes.CONFIRMATION_MODAL
    );
  };

  const getRequest = () => {
    return {
      data: {
        legalRepresentativeData: legalRepresentative,
        companyData: company,
        mainMerchantId: referralIdParam,
        externalSubMerchantId,
        country: params.country,
        ...termsAndConditions,
      },
    };
  };

  const handleChangeStep = (stepName) => {
    setActiveStep(stepName);
  };

  const handleNextStep = () => {
    if (activeStep === stepsName.LEGAL_REPRESENTATIVE) handleChangeStep(stepsName.COMPANY);
    else handleChangeStep(stepsName.TERMS_CONDITIONS);
  };
  const handlePrevStep = () => {
    if (activeStep === stepsName.COMPANY) handleChangeStep(stepsName.LEGAL_REPRESENTATIVE);
    else handleChangeStep(stepsName.COMPANY);
  };
  const hasPrevious = () => {
    if (activeStep !== stepsName.LEGAL_REPRESENTATIVE) return true;
  };

  const steps = [
    {
      label: stepsName.LEGAL_REPRESENTATIVE,
      name: stepsName.LEGAL_REPRESENTATIVE,
      id: `${stepsName.LEGAL_REPRESENTATIVE}Step`,
      onClick: () => handleChangeStep(stepsName.COMPANY),
    },
    { label: stepsName.COMPANY, name: stepsName.COMPANY, id: `${stepsName.COMPANY}Step`, onClick: () => handleChangeStep(stepsName.COMPANY) },
    { label: stepsName.TERMS_CONDITIONS, name: stepsName.TERMS_CONDITIONS, id: `${stepsName.TERMS_CONDITIONS}Step`, onClick: () => handleChangeStep(stepsName.TERMS_CONDITIONS) },
  ];

  const getActiveStep = () => {
    switch (activeStep) {
      case stepsName.LEGAL_REPRESENTATIVE:
        return 0;
      case stepsName.COMPANY:
        return 1;
      case stepsName.TERMS_CONDITIONS:
        return 2;
      default:
        return 0;
    }
  };

  const propsForSteps = {
    hasPrevious,
    handlePrevStep,
    t,
    handleNextStep,
    lastStep,
    activeStep,
    loading,
  };

  return (
    <main className="root-container">
      <div className="mainContainer">
        {referralIdParam ? (
          <div className="signup-container">
            <div className="signup-header">
              <div className="col-lg-12">
                <div className="logo-container">
                  <img src={`${AppUrls.resources.logos}directa-logo-crop-color.png`} alt={companyName} className="logo" />
                </div>
              </div>
            </div>
            <div className="signup-title">
              <h3>{t('titleReferralSignUp')}</h3>
            </div>
            <Stepper steps={steps} getActiveStep={getActiveStep} t={t} />

            {loadingC ? (
              <Loading />
            ) : (
              <div>
                {activeStep === stepsName.LEGAL_REPRESENTATIVE && React.cloneElement(childrenArray[0], { ...propsForSteps })}
                {activeStep === stepsName.COMPANY && React.cloneElement(childrenArray[1], { ...propsForSteps })}
                {activeStep === stepsName.TERMS_CONDITIONS && React.cloneElement(childrenArray[2], { create: handleCreateReferral, ...propsForSteps })}
              </div>
            )}
          </div>
        ) : (
          <NotFound />
        )}
      </div>
    </main>
  );
};
const mapStateToProps = (state) => {
  return {
    legalRepresentative: getLegalRepresentative(state),
    company: getCompany(state),
    termsAndConditions: getTermsAndConditions(state),
    isLoading: isLoading(state),
  };
};
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...GlobalModalActions,
      showSuccess,
      showError,
    },
    dispatch
  );
export default compose(withRouter, withNamespaces(), connect(mapStateToProps, mapDispatchToProps))(SignUpReferrals);
